// Studio carts are evaluated as *scripts* (`new Function`), not modules, so
// top-level `export` is a syntax error. The README documented the module shape
// for years and nova64@0.5.3 still ships it, so that form is widespread — in
// bookmarked snippets, in copied examples, and especially in carts generated by
// assistants trained on that README. Rejecting it outright breaks those carts
// for a reason the raw parser message ("Unexpected token 'export'") never
// explains, so the executor accepts the module shape and strips it instead.
//
// `import` is deliberately NOT stripped: an export is only a declaration
// modifier, so removing it leaves identical behaviour, whereas removing an
// import would leave the cart referencing bindings that were never created —
// it would start, then fail somewhere less obvious. Imports keep failing fast,
// with an error that says why.

// NAMESPACE_MAP is the single source of truth for where each retired flat
// global moved; explainCartError reads it so the advice can never drift from
// the runtime. namespace.js is pure data, so this module stays DOM-free.
import { NAMESPACE_MAP } from './namespace.js';

// Characters after which a `export` keyword can legally begin a statement.
const STATEMENT_BOUNDARY = new Set([';', '}', '{']);

// A `/` is division (not a regex literal) when it follows a value. Anything else
// -- an operator, a comma, an open bracket, or one of these keywords -- means a
// regex can start there. Without this, a regex containing a quote or a brace
// (`/["']/`, `/\{/`) desynchronises the string and brace tracking below.
const VALUE_END = /[A-Za-z0-9_$)\]]/;
const REGEX_KEYWORDS =
  /\b(return|typeof|instanceof|case|in|of|delete|void|new|do|else|yield|await)$/;

/**
 * Remove top-level `export` syntax from cart source.
 *
 * Deliberately string-, template- and comment-aware: a naive regex corrupts any
 * cart that merely *mentions* export inside a string (`print('export failed')`)
 * or a comment. Only `export` appearing as the first token of a top-level
 * statement is touched; anything nested inside braces is left alone.
 *
 * Handles `export function/const/let/var/class`, `export default`, and
 * re-export lists (`export { init, update, draw };`).
 *
 * @param {string} src
 * @returns {string} src with top-level export syntax removed
 */
export function stripTopLevelExports(src) {
  let out = '';
  let depth = 0; // brace depth, counted outside strings/comments
  let prevSignificant = ''; // last non-whitespace, non-comment char emitted
  let sawNewline = true; // newline seen since prevSignificant (ASI boundary)
  const templateStack = []; // brace depth at each open `${`, for nested templates
  let i = 0;

  const atStatementStart = () =>
    depth === 0 &&
    templateStack.length === 0 &&
    (prevSignificant === '' || STATEMENT_BOUNDARY.has(prevSignificant) || sawNewline);

  while (i < src.length) {
    const ch = src[i];
    const next = src[i + 1];

    // ── comments: copied through verbatim, never scanned for `export` ──
    if (ch === '/' && next === '/') {
      const end = src.indexOf('\n', i);
      const stop = end === -1 ? src.length : end;
      out += src.slice(i, stop);
      i = stop;
      continue;
    }
    if (ch === '/' && next === '*') {
      const end = src.indexOf('*/', i + 2);
      const stop = end === -1 ? src.length : end + 2;
      out += src.slice(i, stop);
      i = stop;
      continue;
    }

    // ── regex literals: copied through so their contents never affect state ──
    if (ch === '/' && (!VALUE_END.test(prevSignificant) || REGEX_KEYWORDS.test(out))) {
      let j = i + 1;
      let inClass = false;
      let closed = false;
      while (j < src.length) {
        const c = src[j];
        if (c === '\\') {
          j += 2;
          continue;
        }
        if (c === '\n') break; // unterminated — fall through and treat as division
        if (c === '[') inClass = true;
        else if (c === ']') inClass = false;
        else if (c === '/' && !inClass) {
          closed = true;
          break;
        }
        j += 1;
      }
      if (closed) {
        while (j + 1 < src.length && /[a-z]/.test(src[j + 1])) j += 1; // flags
        out += src.slice(i, j + 1);
        i = j + 1;
        prevSignificant = '/';
        sawNewline = false;
        continue;
      }
    }

    // ── quoted strings: copied through, escapes respected ──
    if (ch === '"' || ch === "'") {
      let j = i + 1;
      while (j < src.length) {
        if (src[j] === '\\') j += 2;
        else if (src[j] === ch) break;
        else j += 1;
      }
      out += src.slice(i, Math.min(j + 1, src.length));
      i = j + 1;
      prevSignificant = ch;
      sawNewline = false;
      continue;
    }

    // ── template literals: `${ … }` substitutions re-enter normal scanning ──
    if (ch === '`') {
      let j = i + 1;
      while (j < src.length) {
        if (src[j] === '\\') {
          j += 2;
          continue;
        }
        if (src[j] === '`') break;
        if (src[j] === '$' && src[j + 1] === '{') {
          // Emit up to the substitution, then let the main loop handle it so a
          // nested template or object literal inside it is tracked correctly.
          out += src.slice(i, j + 2);
          templateStack.push(depth);
          i = j + 2;
          break;
        }
        j += 1;
      }
      if (j < src.length && src[j] === '$') continue; // entered a substitution
      out += src.slice(i, Math.min(j + 1, src.length));
      i = j + 1;
      prevSignificant = '`';
      sawNewline = false;
      continue;
    }

    // ── brace tracking ──
    if (ch === '{') depth += 1;
    if (ch === '}') {
      if (templateStack.length && templateStack[templateStack.length - 1] === depth) {
        // Closing a `${ … }` — resume template-literal scanning.
        templateStack.pop();
        out += ch;
        i += 1;
        let j = i;
        while (j < src.length) {
          if (src[j] === '\\') {
            j += 2;
            continue;
          }
          if (src[j] === '`') break;
          if (src[j] === '$' && src[j + 1] === '{') {
            out += src.slice(i, j + 2);
            templateStack.push(depth);
            i = j + 2;
            break;
          }
          j += 1;
        }
        if (j < src.length && src[j] === '$') continue;
        out += src.slice(i, Math.min(j + 1, src.length));
        i = j + 1;
        prevSignificant = '`';
        sawNewline = false;
        continue;
      }
      depth = Math.max(0, depth - 1);
    }

    // ── the actual rewrite ──
    if (ch === 'e' && atStatementStart() && /^export\b/.test(src.slice(i))) {
      let j = i + 'export'.length;
      while (j < src.length && /\s/.test(src[j])) j += 1;

      if (src[j] === '{') {
        // `export { a, b };` / `export { a } from 'x';` — drop the statement.
        let k = j + 1;
        while (k < src.length && src[k] !== '}') k += 1;
        k += 1; // past `}`
        while (k < src.length && /\s/.test(src[k])) k += 1;
        if (/^from\b/.test(src.slice(k))) {
          k += 'from'.length;
          while (k < src.length && /\s/.test(src[k])) k += 1;
          const quote = src[k];
          if (quote === '"' || quote === "'") {
            k += 1;
            while (k < src.length && src[k] !== quote) k += src[k] === '\\' ? 2 : 1;
            k += 1;
          }
        }
        while (k < src.length && /\s/.test(src[k])) k += 1;
        if (src[k] === ';') k += 1;
        i = k;
        prevSignificant = ';';
        sawNewline = false;
        continue;
      }

      if (/^default\b/.test(src.slice(j))) {
        j += 'default'.length;
        while (j < src.length && /\s/.test(src[j])) j += 1;
      }
      i = j; // drop `export ` (and `default `), keep the declaration itself
      continue;
    }

    out += ch;
    if (/\s/.test(ch)) {
      if (ch === '\n') sawNewline = true;
    } else {
      prevSignificant = ch;
      sawNewline = false;
    }
    i += 1;
  }

  return out;
}

const WRAPPER_TAIL = `
; return {
  init: typeof init !== "undefined" ? init : null,
  update: typeof update !== "undefined" ? update : null,
  draw: typeof draw !== "undefined" ? draw : null,
  render: typeof render !== "undefined" ? render : null
};`;

export function createStudioCartFunction(userCode) {
  if (typeof userCode !== 'string') {
    throw new TypeError('Studio cart code must be a string');
  }

  try {
    return new Function(`${userCode}${WRAPPER_TAIL}`);
  } catch (error) {
    if (!(error instanceof SyntaxError)) throw error;

    // Only reached by code that already failed to parse, so the rewrite can
    // never change the behaviour of a cart that was working.
    const stripped = stripTopLevelExports(userCode);
    if (stripped !== userCode) {
      try {
        return new Function(`${stripped}${WRAPPER_TAIL}`);
      } catch (retryError) {
        if (!(retryError instanceof SyntaxError)) throw retryError;
        // Fall through — report against the original source.
      }
    }

    throw new SyntaxError(
      `Invalid Studio cart syntax: ${error.message}. Studio executes scripts: declare function init(), update(dt), and draw() without import or export. Use ES modules only for file-based carts.`,
      { cause: error }
    );
  }
}

export function executeStudioCartCode(userCode) {
  return createStudioCartFunction(userCode)();
}

/**
 * Turn a bare `X is not defined` from a cart into a message that says where X
 * went.
 *
 * The v0.5.0 namespace push retired the flat globals, so a cart written against
 * the old API — one saved in the Studio before the move, one copied from a doc
 * that still taught it, or one an assistant generated from `nova64@0.5.3` —
 * fails with `createCube is not defined` and nothing else. The name is not gone;
 * it moved to `nova64.scene.createCube`, and that is the one fact the error
 * never carried.
 *
 * Returns the original message unchanged when the name is not a retired global,
 * so a genuine typo still reads as a typo.
 *
 * @param {unknown} error
 * @returns {string} a message safe to show the user
 */
export function explainCartError(error) {
  const message = error && typeof error.message === 'string' ? error.message : String(error);
  if (!(error instanceof ReferenceError)) return message;

  const match = /^(\w[\w$]*) is not defined$/.exec(message);
  if (!match) return message;

  const name = match[1];
  const group = Object.keys(NAMESPACE_MAP).find(g => NAMESPACE_MAP[g].includes(name));
  if (!group) return message;

  return (
    `${message} — the flat API was retired in v0.5.0. Use nova64.${group}.${name}(...), ` +
    `or destructure it first: const { ${name} } = nova64.${group};`
  );
}
